import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import colors from '@general/colors';
import { getEventRegistrationURL, getTrusteeEventRegisteredURL } from '@general/entities/event';
import { checkForRegistrationFuture } from '@general/helpers/event.helpers';
import getFavoriteHook from '@hooks/getFavoriteHook';
import FavoriteIcon from '@icons/FavoriteIcon';
import AddToCalendarButton from '@molecules/AddToCalendarButton';
import { BodySmall, H4, LinkRouter } from '@styled';
import CardChip from '../CardChip';
import useIsRegisteredForEvent from '../../../../icsc-app/js/hooks/useIsRegisteredForEvent';

const TitleStyled = styled(H4)`
    transition: color 0.2s ease-in-out;

    &:hover {
        color: ${colors.primary};
    }
`;

const EventListItemTrustee = ({
    event,
    isFavorite: initialIsFavorite,
    showCalendarIcon = false,
    showFavoriteIcon = true,
    chip = {},
    favorited = () => {},
    unfavorited = () => {},
    onClick = () => {},
    showBottomLinkCheck = () => true,
}) => {
    const { isRegistered: isRegisteredForEvent } = useIsRegisteredForEvent(event.code);
    let eventUrl = event.url;
    if (!event.url && event.code) {
        eventUrl = getEventRegistrationURL(event.code);
    }

    if (isRegisteredForEvent) {
        eventUrl = getTrusteeEventRegisteredURL(event.code);
    }

    const useFavorite = getFavoriteHook({ type: 'event', initialIsFavorite });
    const { isFavorite, isLoadingFavorite, saveFavorite, removeFavorite, setIsFavoriteManually } = useFavorite();
    const [isRemoved, setIsRemoved] = useState(false);

    useEffect(() => {
        setIsFavoriteManually(initialIsFavorite);
    }, [initialIsFavorite, setIsFavoriteManually]);

    const onFavorite = () => {
        if (!isFavorite || isRemoved) {
            setIsRemoved(false);
            saveFavorite(event.id);
            favorited(event.id);
            return;
        }

        if (isFavorite) {
            setIsRemoved(true);
            removeFavorite(event.id);
            unfavorited(event.id);
        }
    };

    const isRegistrationFuture = checkForRegistrationFuture({ registrationStart: event.registrationStartDate });
    const showBottomLink = showBottomLinkCheck(event);

    return (
        <div className="list-card py-4 px-2 px-md-0 my-0 w-100">
            <div className="d-flex">
                <div>
                    {event.image && <img alt={event.title} className="m-o p-0 card-img-right" src={event.image} />}
                    {!event.image && (
                        <img alt="" className="m-o p-0 card-img-right" src="/assets/images/event-icon.svg" />
                    )}
                </div>

                <div className="d-flex w-100">
                    <div className="card-block m-0 ml-md-3 p-0 pl-2 pl-sm-0 pl-md-2">
                        <a href={eventUrl} onClick={() => onClick(eventUrl)} onKeyDown={() => onClick(eventUrl)}>
                            <TitleStyled className="card-title d-inline-block">
                                <span className="mr-3">{event.title}</span>
                                {chip.color && chip.text && <CardChip color={chip.color}>{chip.text}</CardChip>}
                            </TitleStyled>
                        </a>

                        {event.registrationOpen && !isRegisteredForEvent && (
                            <div className="mt-0.5rem">
                                <CardChip color="red">Registration Open</CardChip>
                            </div>
                        )}

                        {event.registrationOpen && isRegisteredForEvent && (
                            <div className="mt-0.5rem">
                                <CardChip color="emerald">Already Registered</CardChip>
                            </div>
                        )}

                        {!event.registrationOpen && isRegistrationFuture && (
                            <div className="mt-0.5rem">
                                <CardChip color="brightSun">Save the Date</CardChip>
                            </div>
                        )}

                        <BodySmall className="mt-0.5rem text-background">
                            {event.dateRange}
                            {event.displayLocation ? ` - ${event.displayLocation}` : ''}
                        </BodySmall>

                        {showBottomLink && (
                            <p className="mb-0">
                                <LinkRouter to={`/event-registration/${event.code}`}>Register Now</LinkRouter>
                            </p>
                        )}
                    </div>

                    <div>
                        <div className="d-flex align-items-center">
                            {showCalendarIcon && (
                                <div className="mr-1.5rem" style={{ marginTop: '-5px' }}>
                                    <AddToCalendarButton event={event} />
                                </div>
                            )}

                            {showFavoriteIcon && (
                                <FavoriteIcon
                                    id={event.id}
                                    type="event"
                                    isFavorite={isFavorite}
                                    onClick={onFavorite}
                                    isLoading={isLoadingFavorite}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

EventListItemTrustee.propTypes = {
    event: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        code: PropTypes.string,
        title: PropTypes.string.isRequired,
        image: PropTypes.string,
        beginsAt: PropTypes.string,
        endsAt: PropTypes.string,
        dateRange: PropTypes.string,
        timezone: PropTypes.string,
        url: PropTypes.string,
        address: PropTypes.string,
        city: PropTypes.string,
        stateProvince: PropTypes.string,
        country: PropTypes.string,
        displayLocation: PropTypes.string,
        registrationOpen: PropTypes.bool,
        registrationStartDate: PropTypes.number,
    }).isRequired,

    chip: PropTypes.object,
    isFavorite: PropTypes.bool.isRequired,
    showCalendarIcon: PropTypes.bool,
    showFavoriteIcon: PropTypes.bool,
    favorited: PropTypes.func,
    onClick: PropTypes.func,
    showBottomLinkCheck: PropTypes.func,
    unfavorited: PropTypes.func,
};

export default EventListItemTrustee;
